import { useCallback, useEffect, useState } from 'react';
import {
    Button, FieldError, Input, Label, ListBox, ListBoxItem, Modal, SearchField,
    Select, TextArea, TextField
} from '@heroui/react';

import { Link } from 'react-router-dom';
import CollectionThumb from './CollectionThumb';
import { SkeletonThumbGrid } from '../../common/Skeleton';
import { EmptyResult } from '../../common/LoadMore';
import Pagination from '../../common/Pagination';
import Notice from '../../common/Notice';
import useValidation from '../../common/useValidation';
import { useBrowseState, useSearchTerm } from '../../common/useBrowseState';
// feat/topics — D47 και D48.
import SuggestionsField from '../../common/SuggestionsField';
import SuggestedCollections from './SuggestedCollections';
import { getTopics } from '../../../services/topics.service';
import { TOPICS } from '../../../i18n/el';
import userService from '../../../services/auth.service';
import { fieldErrorsOf } from '../../../services/apiClient';
import * as check from '../../../services/validation';
import ViewTab from '../../common/ViewTab';
import { isFiltering, SORTS } from '../../../services/browseState';
import { messageOf, SORT_OPTIONS, P1_5_TEXT, SELECT_PLACEHOLDER } from '../../../i18n/el';

// ΛΑ-06: συγκεντρωτική προβολή των συλλογών στις οποίες συμμετέχει ο χρήστης.
// ΛΑ-05: δημιουργία συλλογής με τίτλο, περιγραφή, θεματικό αντικείμενο και
// επίπεδο πρόσβασης.

const VISIBILITIES = [
    { id: 'private', label: 'Ιδιωτική, ορατή μόνο στα μέλη' },
    { id: 'public', label: 'Δημόσια, ορατή σε όλους' }
];

const EMPTY_FORM = { title: '', description: '', topic: '', visibility: 'private' };

/**
 * Ένα σκέλος της εναλλαγής προβολής.
 *
 * Δεν χρησιμοποιείται το `Button` του HeroUI: οι παραλλαγές του είναι φτιαγμένες
 * για **ενέργειες** και η καθεμία φέρνει τη δική της σημασία. Εδώ χρειάζεται
 * κατάσταση επιλογής, που είναι άλλο πράγμα, και το `aria-pressed` είναι αυτό που
 * τη μεταφέρει στην υποστηρικτική τεχνολογία.
 */

export default function CollectionsPage() {
    const [collections, setCollections] = useState([]);
    // ΛΑ-06: αναζήτηση, ταξινόμηση και σελίδα ζουν στη **διεύθυνση** και
    // εκτελούνται στον **διακομιστή**. Το φιλτράρισμα στον πελάτη αφορούσε μόνο
    // τα φορτωμένα: μια συλλογή στη θέση 21 δεν βρισκόταν, και το μήνυμα κενού
    // αποτελέσματος έπρεπε να δηλώνει αυτό το εύρος για να μην ψεύδεται.
    const { state, query, setFilter, setPage, setSort } = useBrowseState({
        sorts: SORTS.collections
    });
    const [term, setTerm] = useSearchTerm(
        state.q,
        // Αντικατάσταση και όχι προσθήκη: αλλιώς κάθε γράμμα γίνεται καταχώρηση
        // ιστορικού και το κουμπί επιστροφής γίνεται άχρηστο.
        useCallback((value) => setFilter({ q: value }, { replace: true }), [setFilter])
    );
    // ΛΑ-07: οι αρχειοθετημένες συλλογές έχουν δική τους προβολή. Το API επιστρέφει
    // τις δύο ομάδες χωριστά και δεν υπάρχει «όλες μαζί»: η αρχειοθέτηση σημαίνει
    // «έξω από την καθημερινή ροή», οπότε μια ανάμεικτη λίστα θα ακύρωνε τον σκοπό της.
    // **Μία** μεταβλητή για τρεις προβολές, όχι δύο σημαίες: δύο booleans θα
    // επέτρεπαν τον άκυρο συνδυασμό «αρχειοθετημένες ΚΑΙ δημόσιες», δηλαδή
    // λανθάνουσα κατάσταση που δεν εκφράζεται στην οθόνη.
    //
    // Η προβολή **Δημόσιες** είναι η ανακάλυψη που έλειπε: ο Πίνακας 1 δίνει
    // δικαίωμα ανάγνωσης δημόσιας συλλογής ακόμη και σε επισκέπτη, και καμία
    // οθόνη δεν απαριθμούσε ποτέ δημόσιες συλλογές — το δικαίωμα ασκούνταν μόνο
    // από κοινοποιημένο σύνδεσμο.
    const [view, setView] = useState('mine');
    const showArchived = view === 'archived';
    const showPublic = view === 'public';
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    // Η σελιδοποίηση του API: `{ page, limit, total, totalPages }`.
    const [meta, setMeta] = useState(null);

    const [isCreateOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [isSaving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});

    const validation = useValidation();

    // ── D47: το ελεγχόμενο λεξιλόγιο ────────────────────────────────────────────
    //
    // Φορτώνεται **μία φορά** με τη σελίδα και όχι με το άνοιγμα της φόρμας: είναι
    // μικρό, δεν αλλάζει μέσα στη συνεδρία, και ο επιλογέας πρέπει να είναι
    // γεμάτος τη στιγμή που ανοίγει ο διάλογος — κατάλογος που φτάνει μισό
    // δευτερόλεπτο αργότερα διαβάζεται ως «δεν υπάρχουν θέματα».
    //
    // Η αποτυχία **καταπίνεται**: ο κατάλογος μένει κενός και η φόρμα το λέει με
    // σύνδεσμο. Μήνυμα σφάλματος για βοηθητικό κατάλογο θα έστελνε τον χρήστη να
    // διορθώσει τη συλλογή του ενώ εκείνη είναι μια χαρά.
    const [topicNames, setTopicNames] = useState([]);

    useEffect(() => {
        getTopics().then(
            // Ίδιο σφάλμα, δεύτερο σημείο: ο `apiClient` δίνει ήδη τον πίνακα.
            (data) => setTopicNames((data ?? []).map((topic) => topic.name)),
            () => setTopicNames([])
        );
    }, []);

    // ΛΑ-06: το φίλτρο θέματος ζει **στη διεύθυνση**, ώστε ο σύνδεσμος από τη
    // σελίδα των θεματικών αντικειμένων να είναι κοινοποιήσιμος.
    const topicFilter = state.topic ?? '';

    /**
     * Φορτώνει **τη** σελίδα που λέει η διεύθυνση.
     *
     * Δεν υπάρχει πλέον προσάρτηση: κάθε σελίδα αντικαθιστά την προηγούμενη.
     * Μαζί της έφυγε και το φιλτράρισμα διπλότυπων που χρειαζόταν η προσάρτηση —
     * όταν οι σελίδες μετατοπίζονταν, η ίδια εγγραφή μπορούσε να έρθει δεύτερη
     * φορά και να δώσει διπλό κλειδί React.
     */
    const fetchCollections = useCallback(() => {
        setLoading(true);

        // Το `?topic=` **τέμνεται** με το εύρος στον διακομιστή, ποτέ δεν το
        // αντικαθιστά: ο κανόνας ορατότητας δεν γίνεται πιο γενναιόδωρος επειδή ο
        // χρήστης ζήτησε θέμα.
        userService.getCollections({ archived: showArchived, scope: showPublic ? 'public' : 'mine', query }).then(
            ({ data, meta: pageMeta }) => {
                setCollections(data);
                setMeta(pageMeta);
                setLoading(false);
                setError('');
            },
            (apiError) => {
                setError(messageOf(apiError));
                setLoading(false);
            }
        );
    }, [showArchived, showPublic, query]);

    useEffect(() => { fetchCollections(); }, [fetchCollections]);

    const set = (name) => (value) => setForm((current) => ({ ...current, [name]: value }));

    function openCreate() {
        setForm(EMPTY_FORM);
        validation.reset();
        setFormError('');
        setFieldErrors({});
        setCreateOpen(true);
    }

    // Ο διακομιστής απαιτεί **2** χαρακτήρες. Ο παλιός έλεγχος κοίταζε μόνο αν το
    // πεδίο είναι κενό, οπότε τίτλος ενός χαρακτήρα περνούσε τοπικά και
    // απορριπτόταν με 422 μετά την υποβολή.
    const localTitle = check.collectionTitle(form.title);
    const titleMessage = fieldErrors.title ?? validation.show('title', localTitle);

    function createCollection(event) {
        event.preventDefault();
        validation.markSubmitted();
        setFormError('');
        setFieldErrors({});

        if (localTitle) return;

        setSaving(true);
        userService.createCollection(form).then(
            () => {
                // Επαναφόρτωση αντί για τοπική προσθήκη. Με την αναζήτηση και την
                // ταξινόμηση στον διακομιστή, η θέση της νέας κάρτας δεν είναι
                // πλέον «πρώτη»: εξαρτάται από τον ενεργό όρο και τη σειρά. Η
                // τοπική προσθήκη θα την έβαζε στην κορυφή ακόμη κι όταν δεν
                // ταιριάζει με το φίλτρο, δηλαδή θα έδειχνε κάτι που η επόμενη
                // φόρτωση θα εξαφάνιζε.
                if (view === 'mine') fetchCollections();
                setCreateOpen(false);
                setSaving(false);
                validation.reset();
                setForm(EMPTY_FORM);
            },
            (apiError) => {
                setFormError(messageOf(apiError));
                setFieldErrors(fieldErrorsOf(apiError));
                setSaving(false);
            }
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <SearchField
                    value={term}
                    onChange={setTerm}
                    className="w-full sm:max-w-sm"
                    aria-label="Αναζήτηση συλλογών"
                >
                    <Label>Αναζήτηση</Label>
                    <SearchField.Group>
                        <SearchField.SearchIcon />
                        <SearchField.Input placeholder="Τίτλος, θέμα ή περιγραφή" />
                        <SearchField.ClearButton />
                    </SearchField.Group>
                </SearchField>

                {/* Εναλλαγή κατάστασης, όχι δύο ενέργειες. Τρεις κανόνες:

                    1. **Καμία σημασιολογική απόχρωση.** Οι ενεργές συλλογές δεν είναι
                       «επιτυχία» και οι αρχειοθετημένες δεν είναι «προειδοποίηση». Το
                       πράσινο και το πορτοκαλί σημαίνουν ήδη κάτι στις ειδοποιήσεις,
                       και μια δεύτερη σημασία τα ακυρώνει και στις δύο θέσεις.
                    2. **Το ενεργό σκέλος παίρνει το πρωτεύον χρώμα, το ανενεργό μένει
                       ουδέτερο.** Δεν χρησιμοποιείται το `outline`, γιατί το δικό μας
                       `outline` έχει μωβ κείμενο και μωβ περίγραμμα: και τα δύο σκέλη
                       θα έφεραν το χρώμα της μάρκας και η διάκριση θα ήταν αδύναμη.
                    3. **Εικονίδιο και στα δύο**, ώστε η κατάσταση να μη μεταφέρεται
                       μόνο με χρώμα (WCAG 1.4.1), μαζί με το `aria-pressed`. */}
                <div
                    role="group"
                    aria-label="Προβολή συλλογών"
                    className="flex shrink-0 items-center gap-1 rounded-full border border-border bg-surface p-1"
                >
                    {/* Η εναλλαγή προβολής **μηδενίζει τη σελίδα**, για τον ίδιο
                        λόγο που τη μηδενίζει κάθε φίλτρο: οι αρχειοθετημένες
                        είναι λίγες, και η σελίδα 3 της μιας προβολής δεν υπάρχει
                        στην άλλη — ο χρήστης θα έβλεπε κενό και θα συμπέραινε
                        ότι δεν υπάρχουν αρχειοθετημένες συλλογές. */}
                    <ViewTab
                        icon="fa-solid fa-layer-group"
                        isActive={view === 'mine'}
                        onPress={() => { setView('mine'); setFilter({}); }}
                    >
                        Ενεργές
                    </ViewTab>
                    <ViewTab
                        icon="fa-solid fa-box-archive"
                        isActive={view === 'archived'}
                        onPress={() => { setView('archived'); setFilter({}); }}
                    >
                        Αρχειοθετημένες
                    </ViewTab>
                    <ViewTab
                        icon="fa-solid fa-globe"
                        isActive={view === 'public'}
                        onPress={() => { setView('public'); setFilter({}); }}
                    >
                        Δημόσιες
                    </ViewTab>
                </div>
            </div>

            {/* ΛΑ-06: η απαίτηση ονομάζει την ταξινόμηση **μαζί** με το
                φιλτράρισμα, και έλειπε — η σειρά ήταν σταθερά κατά ημερομηνία
                μεταβολής. Γίνεται στον διακομιστή, ώστε να αφορά ολόκληρο το
                σύνολο και όχι τη φορτωμένη σελίδα. */}
            <Select placeholder={SELECT_PLACEHOLDER}
                selectedKey={`${state.sort}:${state.order}`}
                onSelectionChange={(key) => setSort(String(key))}
                className="w-full sm:max-w-xs"
            >
                <Label>Ταξινόμηση</Label>
                <Select.Trigger>
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox items={SORT_OPTIONS.collections}>
                        {(item) => <ListBoxItem id={item.id}>{item.label}</ListBoxItem>}
                    </ListBox>
                </Select.Popover>
            </Select>

            {view === 'mine' && (
                <Button onPress={openCreate} className="self-start">
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                    Νέα συλλογή
                </Button>
            )}

            {error && <Notice status="danger" title="Η φόρτωση απέτυχε">{error}</Notice>}

            {loading && <SkeletonThumbGrid count={4} />}

            {!loading && !error && collections.length === 0 && (
                // Χωρίς το `hasMore`: ο έλεγχος έγινε στον διακομιστή πάνω σε
                // ολόκληρο το σύνολο, οπότε το «καμία αντιστοιχία» είναι πλέον
                // αληθές και δεν χρειάζεται να δηλωθεί το εύρος του (ΜΛΑ-03).
                <EmptyResult
                    isSearching={isFiltering(state)}
                    emptyMessage={P1_5_TEXT.viewEmpty[view]}
                />
            )}

            {/* ⚠ **D48: ΜΟΝΟ στην προβολή «οι δικές μου».** Η ανακάλυψη και οι
                αρχειοθετημένες είναι **σκόπιμες αναζητήσεις**: ο χρήστης ρώτησε κάτι
                συγκεκριμένο, και μια κάρτα προτάσεων ανάμεσα στα αποτελέσματα θα
                διαβαζόταν ως μέρος τους. Η αρχική είναι το μόνο σημείο όπου δεν
                ρώτησε τίποτα. */}
            {!showArchived && !showPublic && !isFiltering(state) && <SuggestedCollections />}

            {!loading && collections.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                    {collections.map((item) => (
                        <CollectionThumb
                            key={item.id}
                            id={item.id}
                            title={item.title}
                            topic={item.topic}
                            description={item.description}
                            visibility={item.visibility}
                            archived={item.archived}
                            memberCount={item.memberCount}
                            resourceCount={item.resourceCount}
                        />
                    ))}
                </div>
            )}

            {!loading && <Pagination meta={meta} onPage={setPage} noun="συλλογές" />}

            {/* ⚠ **Υπό συνθήκη, και είναι διόρθωση ελαττώματος.** Το `isOpen` γίνεται
                ψευδές μετά από **επιτυχή δημιουργία**, δηλαδή από πηγή εκτός του
                διαλόγου, και το `Modal.Root` του HeroUI κλείνει μόνο όταν το κλείσιμο
                το ξεκινά το ίδιο. Ο κανόνας: `ResourceItem` και `CollectionTasks`. */}
            {isCreateOpen && (
            <Modal.Root isOpen onOpenChange={setCreateOpen}>
                <Modal.Backdrop>
                    <Modal.Container>
                        <Modal.Dialog>
                            {/* **Η φόρμα ζει ΜΕΣΑ στο σώμα, ποτέ γύρω από αυτό.**
                                Τυλιγμένη γύρω από κεφαλίδα, σώμα και υποσέλιδο, σπάει την
                                αλυσίδα flex του διαλόγου: το σώμα παύει να είναι άμεσο παιδί
                                του, δεν κληρονομεί ύψος, **δεν κυλάει ποτέ**, και το
                                υποσέλιδο βγαίνει έξω από τον διάλογο που το κόβει — τα
                                κουμπιά υπάρχουν και είναι απρόσιτα.

                                Το κουμπί υποβολής μένει στο υποσέλιδο και δείχνει τη φόρμα
                                με το `form`: η φόρμα δεν χρειάζεται να περιβάλλει το κουμπί
                                για να το ακούει. Μετρήθηκε στον περιηγητή, και με τις δύο
                                διατάξεις. */}
                                <Modal.Header>
                                    <Modal.Heading>Νέα συλλογή</Modal.Heading>
                                    <Modal.CloseTrigger />
                                </Modal.Header>

                                <Modal.Body className="flex flex-col gap-4">
                                    <form id="create-collection-form" onSubmit={createCollection} noValidate className="flex flex-col gap-4">
                                    {formError && <Notice status="danger" title="Η δημιουργία απέτυχε">{formError}</Notice>}

                                    <TextField
                                        name="title"
                                        value={form.title}
                                        onChange={set('title')}
                                        onBlur={validation.touch('title')}
                                        isInvalid={Boolean(titleMessage)}
                                        autoFocus
                                    >
                                        <Label>Τίτλος</Label>
                                        <Input />
                                        <FieldError>{titleMessage}</FieldError>
                                    </TextField>

                                    {/* ⚠ **D47: ΤΟ ΘΕΜΑ ΕΙΝΑΙ ΠΛΕΟΝ ΕΠΙΛΟΓΗ, ΟΧΙ ΕΛΕΥΘΕΡΟ
                                        ΚΕΙΜΕΝΟ.** Το πεδίο μένει ορατό και **ανενεργό** αντί
                                        να εξαφανιστεί: δείχνει τι έχει επιλεγεί, και ο
                                        χρήστης που το βλέπει γκρι καταλαβαίνει ότι
                                        συμπληρώνεται από αλλού — ίδιο πρότυπο με το πεδίο
                                        διεύθυνσης του πόρου (**D10**).

                                        `multiple={false}` γιατί η συλλογή έχει **ένα** θέμα,
                                        και `allowFree={false}` γιατί το λεξιλόγιο είναι
                                        ελεγχόμενο — η υπόδειξη το λέει, αντί να υπονοεί ότι
                                        μπορείς να γράψεις δικό σου. */}
                                    <TextField name="topic" value={form.topic} isDisabled>
                                        <Label>{TOPICS.picker}</Label>
                                        <Input />
                                        <FieldError>{fieldErrors.topic}</FieldError>
                                    </TextField>

                                    {topicNames.length === 0 ? (
                                        // **Κενός κατάλογος: μήνυμα με σύνδεσμο, όχι κενό.**
                                        // Κενή περιοχή διαβάζεται ως «φορτώνει» ή ως βλάβη·
                                        // αυτό λέει τι συμβαίνει και πού να πάει ο χρήστης.
                                        <p className="text-sm text-muted">
                                            {TOPICS.pickerEmpty}{' '}
                                            <Link to="/topics" className="font-medium underline">
                                                {TOPICS.pickerEmptyLink}
                                            </Link>
                                        </p>
                                    ) : (
                                        <div className="flex flex-col gap-2">
                                            <SuggestionsField
                                                suggestions={topicNames}
                                                value={form.topic ? [form.topic] : []}
                                                onAdd={set('topic')}
                                                multiple={false}
                                                allowFree={false}
                                                label={TOPICS.picker}
                                                hint={TOPICS.pickerHint}
                                            />
                                            {form.topic && (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    className="self-start"
                                                    onPress={() => set('topic')('')}
                                                >
                                                    {TOPICS.clear}
                                                </Button>
                                            )}
                                        </div>
                                    )}

                                    <TextField name="description" value={form.description} onChange={set('description')}>
                                        <Label>Περιγραφή</Label>
                                        <TextArea rows={3} />
                                    </TextField>

                                    <Select placeholder={SELECT_PLACEHOLDER}
                                        selectedKey={form.visibility}
                                        onSelectionChange={(key) => set('visibility')(String(key))}
                                    >
                                        <Label>Επίπεδο πρόσβασης</Label>
                                        <Select.Trigger>
                                            <Select.Value />
                                            <Select.Indicator />
                                        </Select.Trigger>
                                        <Select.Popover>
                                            <ListBox items={VISIBILITIES}>
                                                {(item) => <ListBoxItem id={item.id}>{item.label}</ListBoxItem>}
                                            </ListBox>
                                        </Select.Popover>
                                    </Select>
                                    </form>
                                </Modal.Body>

                                <Modal.Footer>
                                    <Button variant="outline" type="button" onPress={() => setCreateOpen(false)}>
                                        Ακύρωση
                                    </Button>
                                    <Button type="submit" form="create-collection-form" isPending={isSaving}>Δημιουργία</Button>
                                </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal.Root>
            )}
        </div>
    );
}
